/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Bottom, Main, ShowHide } from './styles';
import Calendarviewcard from '../calendarViewCard';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import { trackAnalyticActivity } from '../../services/analyticsService';
import { StatusListInterface } from '../../interfaces/ProjectInterface';
import { TASK_ANALYTICS } from '../../global/analyticsConstants';
import { TaskModel } from '../../mobx/models/task';
import MyCustomContextMenuV2 from '../customContextMenu';
import { ProjectModel } from '../../mobx/models/project';
import { rootStore } from '../../mobx/rootStore';
import CardHeader from '../calendarViewCard/cardHeader';
import { ROUTES } from '../../routes/urls';
import { useTaskActions } from '../../hooks/useTaskActions';

interface Props {
  dayName: string;
  date: number;
  tasks: TaskModel[];
  completedTasks: TaskModel[];
  loadData: () => void;
  dateString?: string;
  onClickEdit: (item: TaskModel) => void;
  onClickClone: (item: TaskModel) => void;
  OpenModal: (item: TaskModel) => void;
  className?: string;
}

interface DraggableItemProps {
  item: TaskModel;
  loadData: () => void;
  currentProject: ProjectModel;
  onClickTaskDetails: (item: TaskModel) => void;
  onClickClone: (item: TaskModel) => void;
  onClickEdit: (item: TaskModel) => void;
  OpenModal: (item: TaskModel) => void;
  onChangeStatus: (item: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  onClickTaskCheckBox: (item: TaskModel, status: boolean) => void;
  dateString?: string;
}

const DraggableItem = ({
  item,
  OpenModal,
  currentProject,
  loadData,
  onClickClone,
  onClickEdit,
  onClickTaskDetails,
  onChangeStatus,
  onClickTaskCheckBox,
  dateString
}: DraggableItemProps) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item?.id,
    data: { dateString }
  });

  const style: React.CSSProperties = {
    opacity: isDragging ? 0 : 1
  };

  return (
    <MyCustomContextMenuV2
      taskData={item}
      loadData={loadData}
      currentProject={currentProject}
      onClickView={() => onClickTaskDetails(item)}
      onClickClone={() => onClickClone(item)}
      onClickEdit={() => onClickEdit(item)}
      statusList={currentProject?.statusData || []}
      onClickDelete={() => OpenModal(item)}
      onUpdateStatus={onChangeStatus}>
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <Calendarviewcard
          loadData={loadData}
          taskDetails={item}
          currentProject={currentProject}
          onClickCurrentTaskCheckbox={onClickTaskCheckBox}
        />
      </div>
    </MyCustomContextMenuV2>
  );
};

function WeekViewCard(Props: Props) {
  const {
    onClickEdit,
    onClickClone,
    OpenModal,
    dayName,
    date,
    tasks,
    loadData,
    completedTasks,
    dateString,
    className
  } = Props;
  //State
  const [showCompletedTask, setShowCompletedTask] = useState(false);
  //Other variable
  const history = useHistory();
  const { onChangeStatus, onClickTaskCheckBox, confirmationModal } = useTaskActions(loadData);

  const { setNodeRef, isOver } = useDroppable({
    id: dateString || '',
    data: { dateString }
  });

  const onClickTaskDetails = useCallback(
    (task: TaskModel) => {
      const eventProperties = {
        source: 'task list',
        title: task?.name || ''
      };
      trackAnalyticActivity(TASK_ANALYTICS.VIEW, eventProperties);
      rootStore.taskStore.setCurrentTask(task?.id);
      history.push(ROUTES.TASK_DETAILS.replace(':id', task?.projectId || '').replace(':taskId', task?.id || ''));
    },
    [history]
  );

  return (
    <>
      <Main
        ref={setNodeRef}
        isCompletedTasksOpen={showCompletedTask}
        isWeekend={
          (dayName === 'Saturday' || dayName === 'Sunday') && tasks?.length === 0 && completedTasks?.length === 0
        }
        isActive={dateString === dayjs().startOf('day').toDate().toString()}
        isOver={isOver}>
        <CardHeader
          dayName={
            (dayName === 'Saturday' || dayName === 'Sunday') && tasks?.length === 0 && completedTasks?.length === 0
              ? dayName?.slice(0, 3)
              : dayName
          }
          date={date}
        />
        <Bottom className={className}>
          {tasks?.map((item) => (
            <DraggableItem
              key={item?.id}
              item={item}
              loadData={loadData}
              currentProject={item?.project!}
              onClickTaskDetails={onClickTaskDetails}
              onClickClone={onClickClone}
              onClickEdit={() => onClickEdit(item)}
              OpenModal={OpenModal}
              onChangeStatus={onChangeStatus}
              onClickTaskCheckBox={() => onClickTaskCheckBox(item, !item.status)}
              dateString={dateString}
            />
          ))}
          {completedTasks?.length > 0 && (
            <ShowHide
              role='button'
              onClick={() => setShowCompletedTask(!showCompletedTask)}
              className='hide-show-task-text'>
              {showCompletedTask ? 'Hide' : 'Show'} Completed Tasks
            </ShowHide>
          )}
          {showCompletedTask &&
            completedTasks?.map((item) => (
              <DraggableItem
                key={item?.id}
                item={item}
                loadData={loadData}
                currentProject={item?.project!}
                onClickTaskDetails={onClickTaskDetails}
                onClickClone={onClickClone}
                onClickEdit={() => item}
                OpenModal={OpenModal}
                onChangeStatus={onChangeStatus}
                onClickTaskCheckBox={onClickTaskCheckBox}
                dateString={dateString}
              />
            ))}
        </Bottom>
      </Main>
      {confirmationModal}
    </>
  );
}

export default observer(WeekViewCard);
